import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import './laundryStats.css';
import { DateTime } from 'luxon';
import { DUBAI_ZONE } from '../../../utils/dubaiTime';

function LaundryStats() {
  const [dataByUser, setDataByUser] = useState({});
  const [activeWeeks, setActiveWeeks] = useState({});

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await axios.get('/api/laundry/stats');
        const responseData = res.data;

        const initialWeekState = {};
        for (const userId in responseData) {
          const weeks = Object.keys(responseData[userId].weeks).sort();
          const currentWeekKey = getCurrentWeekKey();
          initialWeekState[userId] = weeks.includes(currentWeekKey)
            ? currentWeekKey
            : weeks[weeks.length - 1];
        }

        setDataByUser(responseData);
        setActiveWeeks(initialWeekState);
      } catch (err) {
        console.error('Error fetching laundry stats:', err);
      }
    };

    fetchStats();
  }, []);

  // "Current week" anchored in Asia/Dubai (item 8): the device's local ISO
  // week could differ from Dubai's around midnight/week boundaries. Same
  // 'yyyy-Www' key shape (calendar year + ISO week, as before).
  const getCurrentWeekKey = () => {
    const now = DateTime.now().setZone(DUBAI_ZONE);
    return `${now.year}-W${String(now.weekNumber).padStart(2, '0')}`;
  };

  const handleWeekChange = (userId, direction) => {
    const weeks = Object.keys(dataByUser[userId].weeks).sort();
    const currentIndex = weeks.indexOf(activeWeeks[userId]);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex >= 0 && newIndex < weeks.length) {
      setActiveWeeks((prev) => ({ ...prev, [userId]: weeks[newIndex] }));
    }
  };

  return (
    <div className="laundry-stats-wrapper">
      <h2 className="laundry-stats-title">Warehouse Staff Weekly Stats</h2>

      {Object.entries(dataByUser).map(([userId, user]) => {
        const activeWeek = activeWeeks[userId];
        const weeklyData = user.weeks[activeWeek] || [];

        return (
          <div className="laundry-stats-chart-card" key={userId}>
            <div className="laundry-stats-header">
              <h3 className="laundry-stats-user-name">{user.name}</h3>
            </div>

            <ResponsiveContainer width="100%" height={320}>
              <LineChart
                data={weeklyData}
                margin={{ top: 10, right: 30, left: 0, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="formattedDate"
                  interval={0}
                  angle={-40}
                  textAnchor="end"
                  tick={{ fontSize: 12 }}
                  height={60}
                />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(value) => value}
                />
                <Legend verticalAlign="top" height={36} />
                <Line
                  type="monotone"
                  dataKey="confirmed"
                  stroke="#FF5A60"
                  name="Confirmed Counts"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="packed"
                  stroke="#3b82f6"
                  name="Packed Bags"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>

            <div className="laundry-stats-week-nav-footer">
              <button onClick={() => handleWeekChange(userId, 'prev')}>
                &lt; Prev Week
              </button>
              <button onClick={() => handleWeekChange(userId, 'next')}>
                Next Week &gt;
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default LaundryStats;
